import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { FocusShell } from '@/components/shell/FocusShell'
import { useShellStore } from '@/store/shell-store'
import { useEntitlements } from '@/hooks/useEntitlements'
import { DEFAULT_APPS } from '@/types/focus-shell'
import { colors } from '@/lib/tokens'
import { perf } from '@/lib/perf'
import { SummaLoader } from '@/components/ui/SummaLoader'


function LoadingScreen() {
  return (
    <div style={{
      height: '100vh',
      background: colors.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '16px',
    }}>
      <SummaLoader size={48} />
      <span style={{ color: colors.textSecondary }}>Cargando...</span>
    </div>
  )
}

function AccessDeniedScreen({ onLogout }: { onLogout: () => void }) {
  return (
    <div style={{
      height: '100vh',
      background: colors.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '24px',
    }}>
      <div style={{
        fontSize: '48px',
        lineHeight: 1,
      }}>
        &#128274;
      </div>
      <h1 style={{
        color: colors.textPrimary,
        fontSize: '20px',
        fontWeight: 600,
        margin: 0,
      }}>
        Acceso denegado
      </h1>
      <p style={{
        color: colors.textSecondary,
        fontSize: '14px',
        margin: 0,
        textAlign: 'center',
        maxWidth: '400px',
        lineHeight: '1.5',
      }}>
        Tu cuenta no tiene permiso para acceder a este despliegue de Focus.
        Contacta a tu administrador para solicitar acceso.
      </p>
      <button
        onClick={onLogout}
        style={{
          padding: '8px 24px',
          background: 'transparent',
          border: `1px solid ${colors.border}`,
          borderRadius: '4px',
          color: colors.textSecondary,
          fontSize: '13px',
          cursor: 'pointer',
          marginTop: '8px',
        }}
      >
        Cerrar sesion
      </button>
    </div>
  )
}

export default function ShellPage() {
  const { user, hasLicense, loading, signOut } = useAuth()
  const navigate = useNavigate()
  const { setAvailableApps, setEnableUat, setOrgParams, initSession, loadSession } = useShellStore()

  const { availableApps: entitledApps, isLoading: entitlementsLoading, error: entitlementsError, isForbidden, remoteAppsReady, enableUat, orgParams } = useEntitlements()

  // Sync entitled apps to shell store AND initialize session (once)
  const sessionInitialized = useRef(false)
  useEffect(() => {
    if (!user || !hasLicense || entitlementsLoading || isForbidden || !remoteAppsReady) return
    if (sessionInitialized.current) return
    sessionInitialized.current = true

    const appsToSet = entitlementsError ? DEFAULT_APPS : entitledApps
    if (entitlementsError) {
      console.warn('[Focus] Error loading entitlements, using DEFAULT_APPS as fallback:', entitlementsError)
    }

    setAvailableApps(appsToSet as any)
    setEnableUat(enableUat)
    setOrgParams(orgParams || {})
    initSession()
    loadSession(user.uid)

    perf.measure('focus:total-to-shell', 'focus:login:start')
    perf.summary()
  }, [user, hasLicense, entitledApps, entitlementsLoading, entitlementsError, isForbidden, remoteAppsReady, enableUat, orgParams, setAvailableApps, setEnableUat, setOrgParams, initSession, loadSession])

  useEffect(() => {
    if (!loading && (!user || !hasLicense)) {
      navigate('/login')
    }
  }, [user, hasLicense, loading, navigate])

  if (loading || entitlementsLoading) {
    return <LoadingScreen />
  }

  if (!user || !hasLicense) {
    return <LoadingScreen />
  }

  if (isForbidden) {
    return <AccessDeniedScreen onLogout={signOut} />
  }

  return <FocusShell />
}
